import { reverse } from 'named-urls'
import React from 'react'
import TagManager from 'react-gtm-module'
import { useNavigate } from 'react-router-dom'

import type { TableData } from '@fourthwall/components'
import {
  ConfirmModal,
  PositionChangeModal,
  Toasts,
  useModalContext,
  usePagination,
} from '@fourthwall/components'
import type { Offer } from '@fourthwall/services/dist/models/offer'
import { pluralize } from '@fourthwall/utils/dist/string'
import { useDeleteOfferMutation } from '@mutations'
import { routing } from '@utils/routing'

import { useDuplicateProduct } from '../Product/hooks/useDuplicateProduct'
import { useStatusMutation } from '../Product/hooks/useStatusMutation'
import { useUnarchiveProduct } from '../Product/hooks/useUnarchiveProduct'
import { useCombinedOfferArchivedMutation } from '../hooks/useCombinedOfferArchivedMutation'
import { ChangeToAnotherProductModal } from '../modules/MigrateOffer/modules/ChangeToAnotherProductModal/ChangeToAnotherProductModal'
import { useSubscription } from '../modules/Subscriptions'
import { ProductsView } from './ProductsView'
import { ToggleStatusModal } from './components/ToggleStatusModal/ToggleStatusModal'
import { PER_PAGE } from './const'
import { useAdditionalProducts } from './hooks/useAdditionalProducts'
import { useProducts } from './hooks/useProducts/useProducts'
import { useUpdateCollectionOrderMutation } from './hooks/useUpdateCollectionOrderMutation'
import {
  getCurrentProductPositionIndex,
  getIdsOfAdjacentElements,
  getUpdatedProductPositionIndex,
} from './utils'

export const ProductsContainer = () => {
  const { open, close } = useModalContext()
  const [currentPage, setCurrentPage] = usePagination()
  const { products, page, isLoading, refetchProducts } = useProducts()
  const { updateCollectionOrderMutation } = useUpdateCollectionOrderMutation()
  const { deleteOfferMutation } = useDeleteOfferMutation()
  const { deleteOfferCombinedMutation } = useCombinedOfferArchivedMutation()
  const { statusMutation } = useStatusMutation()
  const navigate = useNavigate()
  const { invalidateSubscriptionData } = useSubscription()
  const { openDuplicateProductModal } = useDuplicateProduct()
  const { openUnarchiveProductModal } = useUnarchiveProduct()

  useAdditionalProducts({
    onCreated: ({ message }) => {
      Toasts.notify(message, { type: 'info' })
    },
  })

  const refresh = () => {
    refetchProducts()
    invalidateSubscriptionData()
  }

  const handleProductPositionChange = (oldIndex: number, newIndex: number) => {
    TagManager.dataLayer({ dataLayer: { event: 'product_drag_and_drop' } })

    const offerId = products[oldIndex].id
    const adjacentElementsIds = getIdsOfAdjacentElements(products, oldIndex, newIndex)

    updateCollectionOrderMutation.mutate([{ slug: 'all' }, { offerId, ...adjacentElementsIds }], {
      onSuccess: () => refetchProducts(),
    })
  }

  const handleDuplicateOffer = (rowData: TableData) => {
    openDuplicateProductModal({
      name: rowData.name,
      id: rowData.id,
      onSuccess: (duplicatedOffer) => {
        refresh()
        navigate(reverse(routing.products.all.product.self, { productId: duplicatedOffer.id }))
      },
      onError: () => {
        open(ChangeToAnotherProductModal, {
          offerId: rowData.id,
          async onConfirm(duplicatedOfferId) {
            navigate(reverse(routing.products.all.product.self, { productId: duplicatedOfferId }))
          },
          onClose() {
            close()
          },
        })
      },
    })
  }

  const handleArchiveClick = (rowData: TableData) => {
    open(ConfirmModal, {
      title: `Are you sure that you want to archive ${rowData.name}?`,
      confirmLabel: 'Archive',
      confirmAppearance: 'destructive',
      onConfirm: () => {
        if (rowData.type === 'COMBINED') {
          deleteOfferCombinedMutation.mutate([rowData.id], {
            onSuccess: () => {
              refresh()
            },
          })
        } else {
          deleteOfferMutation.mutate([{ offerId: rowData.id }], {
            onSuccess: () => {
              refresh()
            },
          })
        }
      },
    })
  }

  const handleUnarchiveClick = (rowData: TableData) => {
    openUnarchiveProductModal({
      name: rowData.name,
      id: rowData.id,
      onSuccess: () => {
        refresh()
      },
    })
  }

  const handleArchiveManyClick = (offerIds: string[], cb?: () => void) => {
    open(ConfirmModal, {
      title: `Are you sure that you want to archive ${offerIds.length} ${pluralize(offerIds.length, 'product')}?`,
      confirmLabel: 'Archive',
      confirmAppearance: 'destructive',
      onConfirm: async () => {
        for (const offerId of offerIds) {
          // eslint-disable-next-line no-await-in-loop
          await deleteOfferMutation.mutateAsync([{ offerId }])
        }

        // this helps getting the correct statuses from the server
        await new Promise((resolve) => {
          setTimeout(resolve, 1_000)
        })
        await refetchProducts()
        cb?.()
        Toasts.notify(`${offerIds.length} ${pluralize(offerIds.length, 'product')} archived`, {
          type: 'info',
        })
      },
    })
  }

  const handleProductPositionSet = (rowData: TableData) => {
    page &&
      open(PositionChangeModal, {
        title: 'Change product position',
        maxPosition: page.elementsTotal,
        currentPosition: getCurrentProductPositionIndex(
          rowData.id,
          products,
          currentPage,
          PER_PAGE,
        ),
        onSubmit: (formValues) => {
          const { type = 'EXACT', exactPosition } = formValues
          const position = getUpdatedProductPositionIndex(type, exactPosition, page.elementsTotal)

          updateCollectionOrderMutation.mutate(
            [{ slug: 'all' }, { offerId: rowData.id, position }],
            {
              onSuccess: () => refetchProducts(),
            },
          )
        },
      })
  }

  const handleToggleMultipleOfferStatus = (offerIds: string[], cb?: () => void) => {
    open(ToggleStatusModal, {
      getCount: () => offerIds?.length,
      onSubmit: async (status: Offer.State['status']) => {
        for (const offerId of offerIds) {
          // eslint-disable-next-line no-await-in-loop
          await statusMutation.mutateAsync({ offerId, params: { status } })
        }

        // this helps getting the correct statuses from the server
        await new Promise((resolve) => {
          setTimeout(resolve, 1_000)
        })
        await refetchProducts()
        cb?.()
        Toasts.notify(`${offerIds.length} products status updated`, { type: 'info' })
      },
    })
  }

  return (
    <ProductsView
      products={products}
      isLoading={isLoading || updateCollectionOrderMutation.isLoading || statusMutation.isLoading}
      currentPage={currentPage}
      totalPages={page?.totalPages}
      totalProducts={page?.elementsTotal}
      onPageChange={setCurrentPage}
      onProductPositionChange={handleProductPositionChange}
      onProductPositionSet={handleProductPositionSet}
      onDuplicateOffer={handleDuplicateOffer}
      onArchiveOffer={handleArchiveClick}
      onUnarchiveOffer={handleUnarchiveClick}
      onArchiveManyOffers={handleArchiveManyClick}
      onToggleMultipleOfferStatus={handleToggleMultipleOfferStatus}
    />
  )
}
